import { SxProps, Theme } from "@mui/material";
import { MessageRoles } from "../../types/frontend";

export const styles = {
  container: (theme: Theme): SxProps<Theme> => ({
    width: "100%",
    maxWidth: "800px",
    minWidth: "800px",
    height: { xs: "100vh", sm: "85vh" },
    borderRadius: 2,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    bgcolor: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
  }),

  contentContainer: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    position: "relative",
  },

  header: {
    px: 2.5,
    py: 2,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid",
    borderColor: "divider",
    bgcolor: "background.paper",
  },

  headerTitle: {
    fontSize: "1rem",
    fontWeight: 200,
  },

  closeButton: {
    color: "text.secondary",
    "&:hover": {
      color: "text.primary",
      bgcolor: "action.hover",
    },
  },

  starterPromptsContainer: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
    gap: { xs: 0.5, sm: 1 },
    mt: { xs: 0, sm: 8 },
    px: { xs: 0.5, sm: 0 },
  },

  starterTitleContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "8px",
    width: "100%",
    flexDirection: { xs: "column", sm: "row" },
    gap: { xs: 1, sm: 0 },
  },

  starterLogo: {
    width: { xs: "28px", sm: "32px" },
    height: { xs: "28px", sm: "32px" },
    marginRight: { xs: 0, sm: "6px" },
    marginBottom: { xs: "2px", sm: 0 },
    marginTop: { xs: "64px", sm: 0 },
  },

  starterTitle: (theme: Theme): SxProps<Theme> => ({
    fontWeight: "bold",
    marginBottom: "4px",
    textAlign: "center",
    fontSize: { xs: "1.1rem", sm: "1.5rem" },
    color: theme.palette.primary.main,
  }),

  starterSubtitle: (theme: Theme): SxProps<Theme> => ({
    color: theme.palette.primary.main,
    marginBottom: "10px",
    textAlign: "center",
    fontSize: { xs: "0.9rem", sm: "1rem" },
    width: "100%",
    fontStyle: "italic",
  }),

  starterFAQLink: {
    display: "block",
    marginBottom: "12px",
    color: "primary.main",
    textDecoration: "none",
    textAlign: "center",
    width: "100%",
    fontSize: { xs: "0.95rem", sm: "1rem" },
    "&:hover": {
      textDecoration: "underline",
    },
  },

  starterPromptsTitle: (theme: Theme): SxProps<Theme> => ({
    fontSize: { xs: "0.95rem", sm: "1.05rem" },
    fontWeight: 700,
    color: theme.palette.primary.main,
    mb: 1,
    textAlign: "center",
    width: "100%",
  }),

  starterPromptButton: (theme: Theme): SxProps<Theme> => ({
    borderRadius: "8px",
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.primary,
    textTransform: "none",
    fontSize: { xs: "0.75rem", sm: "0.85rem" },
    px: { xs: 2, sm: 2.5 },
    py: { xs: 0.5, sm: 0.75 },
    boxShadow: "none",
    minWidth: "unset",
    width: "auto",
    maxWidth: { xs: "100%", sm: "none" },
    mb: { xs: 1, sm: 0 },
    "&:hover": {
      backgroundColor: theme.palette.grey[300],
      boxShadow: "none",
    },
    "&:focus, &.MuiButtonBase-root": {
      boxShadow: "none",
    },
  }),

  messageList: {
    overflow: "auto",
    px: 2.5,
    py: 2,
    display: "flex",
    flexDirection: "column",
  },

  messageItem: (role: "user" | "assistant", theme: Theme): SxProps<Theme> => ({
    display: "block",
    mb: 1.5,
    bgcolor: role === "user" ? theme.palette.grey[200] : "transparent",
    borderRadius: 1.5,
    py: 1,
    px: 1.5,
    border: "1px solid",
    borderColor: role === "user" ? theme.palette.grey[100] : "transparent",
    alignSelf: role === "user" ? "flex-end" : "flex-start",
    width: "fit-content",
    maxWidth: "95%",
  }),

  messageRole: {
    mb: 0.5,
    fontSize: "0.75rem",
    fontWeight: 600,
  },

  messageContent: (
    role: typeof MessageRoles[keyof typeof MessageRoles],
    theme: Theme
  ): SxProps<Theme> => ({
    bgcolor:
      role === MessageRoles.User
        ? theme.palette.primary.main
        : theme.palette.secondary.light,
    color:
      role === MessageRoles.User
        ? theme.palette.primary.contrastText
        : theme.palette.text.primary,
    fontSize: "0.8rem",
    lineHeight: 1.8,
    maxWidth: "80%",
    p: 1.5,
    borderRadius: 2,
    "& p, & ul, & ol, & li, & pre, & code": {
      margin: 0,
      padding: 0,
      fontSize: "inherit",
      lineHeight: "inherit",
    },
    "& a, & p a, & li a": {
      color: `${theme.palette.primary.main} !important`,
      textDecoration: "underline",
      fontWeight: 500,
    },
    "& ul, & ol": {
      paddingLeft: "2em",
    },
    "& pre": {
      background: theme.palette.grey[100],
      borderRadius: 2,
      padding: "0.5em",
      fontSize: "0.72rem",
      overflowX: "auto",
    },
    "& table": {
      borderCollapse: "collapse",
      width: "100%",
      margin: "1em 0",
      fontSize: "0.9em",
    },
    "& th, & td": {
      border: `1px solid ${theme.palette.grey[300]}`,
      padding: "8px",
      textAlign: "left",
    },
    "& th": {
      backgroundColor: theme.palette.grey[100],
      fontWeight: 600,
    },
    "& tr:nth-of-type(even)": {
      backgroundColor: theme.palette.grey[50],
    },
    "& tr:hover": {
      backgroundColor: theme.palette.grey[100],
    },
  }),

  loadingMessage: {
    mb: 1.5,
    bgcolor: "transparent",
    borderRadius: "12px",
    px: 2,
    py: 1,
    minHeight: "unset",
    height: "32px",
    display: "flex",
    alignItems: "center",
    width: "fit-content",
    maxWidth: "100px",
    alignSelf: "flex-start",
  },
  inputContainer: (theme: Theme): SxProps<Theme> => ({
    px: theme.spacing(1.5),
    py: 0,
    bgcolor: "transparent",
    color: theme.palette.primary.main,
    width: "100%",
    zIndex: 10,
    mt: 0,
    mb: 1,
  }),

  inputField: (
    theme: Theme,
    hasAttachments: boolean = false
  ): SxProps<Theme> => ({
    "& .MuiOutlinedInput-root": {
      bgcolor: theme.palette.grey[100],
      color: theme.palette.primary.main,
      borderRadius: "25px",
      minHeight: "56px",
      position: "relative",
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
      "& .MuiInputBase-input": {
        fontSize: "0.9rem",
        color: theme.palette.primary.main,
        lineHeight: 1.5,
        padding: hasAttachments ? "20px 2px 10px 2px" : "0px 2px 10px 2px",
        mb: "40px",
        verticalAlign: "top",
        pr: 0,
        maxHeight: "150px",
        overflowY: "auto",
      },
      "& .MuiInputAdornment-root": {
        position: "absolute",
        color: theme.palette.primary.main,
        bottom: "14px",
        m: 0,
        height: "auto",
        "&.MuiInputAdornment-positionStart": {
          left: "10px",
        },
        "&.MuiInputAdornment-positionEnd": {
          right: "10px",
        },
      },
    },
  }),

  getInputStyles: (
    isFocused: boolean,
    hasInput: boolean,
    theme: Theme,
    hasAttachments: boolean = false
  ): SxProps<Theme> => {
    const inputFieldStyles = styles.inputField(theme, hasAttachments) as Record<
      string,
      any
    >;
    return {
      ...inputFieldStyles,
      "& .MuiOutlinedInput-root": {
        ...(inputFieldStyles["& .MuiOutlinedInput-root"] || {}),
        "&:hover .MuiOutlinedInput-notchedOutline": {
          border: "none", // no border on hover
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          border: "none", // no border on focus
        },
        "& .MuiOutlinedInput-input": {
          color: "#072531",
        },
      },
    };
  },

  sendButton: (theme: Theme): SxProps<Theme> => ({
    width: "40px",
    height: "40px",
    borderRadius: "100%",
    bgcolor: theme.palette.grey[300],
    color: "white",
    "&.Mui-disabled": {
      bgcolor: theme.palette.grey[300],
      color: "white",
    },
    transition: "all 0.2s ease-in-out",
    position: "relative",
    zIndex: 1,
  }),

  sendButtonActive:
    (isFocused: boolean, hasInput: boolean, isLoading: boolean) =>
    (theme: Theme): SxProps<Theme> => {
      const active = (isFocused && hasInput) || isLoading;
      return {
        width: "40px",
        height: "40px",
        minWidth: "40px",
        minHeight: "40px",
        borderRadius: "100%",
        transition: "all 0.2s ease-in-out",
        position: "relative",
        zIndex: 1,
        bgcolor: active
          ? theme.palette.primary.main + "E6" // ~90% opacity
          : theme.palette.grey[400] + "80", // ~50% opacity
        color: "white",
        "&:hover": {
          bgcolor: active
            ? theme.palette.primary.main
            : theme.palette.grey[400] + "80",
        },
        "&.Mui-disabled": {
          bgcolor: theme.palette.grey[400] + "4D", // ~30% opacity
          color: "white",
        },
      };
    },

  searchButton:
    (isSearchOn: boolean) =>
    (theme: Theme): SxProps<Theme> => ({
      width: "auto",
      height: "40px",
      minWidth: "40px",
      minHeight: "40px",
      borderRadius: "10px",
      bgcolor: isSearchOn ? theme.palette.primary.main : "transparent",
      color: isSearchOn
        ? theme.palette.common.white
        : theme.palette.primary.main,
      padding: "0 16px",
      gap: "2px",
      fontWeight: "bold",
      opacity: isSearchOn ? 0.9 : 1,
      "&.Mui-disabled": {
        bgcolor: "transparent",
        color: theme.palette.primary.main,
      },
      "&:hover": {
        bgcolor: isSearchOn
          ? theme.palette.primary.main
          : theme.palette.grey[200],
        color: isSearchOn
          ? theme.palette.common.white
          : theme.palette.primary.main,
        opacity: 1,
      },
      "& svg": {
        width: "24px",
        height: "24px",
      },
      "& .MuiButton-label": {
        display: "flex",
        alignItems: "center",
        gap: "2px",
      },
    }),

  attachmentChip: (theme: Theme): SxProps<Theme> => ({
    bgcolor: theme.palette.primary.main,
    color: theme.palette.common.white,
    "&:hover": {
      bgcolor: theme.palette.primary.light,
      color: theme.palette.primary.main,
    },
  }),

  attachmentChipClickable: (theme: Theme): SxProps<Theme> => ({
    fontSize: "0.7rem",
    height: "24px",
    "& .MuiChip-label": {
      px: 1,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "160px",
    },
  }),

  attachmentChipDeletable: (theme: Theme): SxProps<Theme> => ({
    "& .MuiChip-deleteIcon": {
      color: theme.palette.common.white,
      transition: "color 0.2s",
    },
    "&:hover .MuiChip-deleteIcon": {
      color: theme.palette.primary.main,
    },
  }),

  attachmentChipContainer: (theme: Theme): SxProps<Theme> => ({
    position: "absolute",
    top: theme.spacing(1),
    left: theme.spacing(1.5),
    right: theme.spacing(1.5),
    display: "flex",
    flexWrap: "nowrap",
    gap: theme.spacing(1),
    zIndex: 1,
    pointerEvents: "none",
    overflowX: "auto",
    overflowY: "hidden",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    borderRadius: "25px",
  }),

  attachmentChipMessageContainer: (theme: Theme): SxProps<Theme> => ({
    mt: 1,
    width: "100%",
    maxWidth: "100%",
    display: "flex",
    flexDirection: "row",
    gap: 1,
    overflowX: "auto",
    overflowY: "hidden",
    borderRadius: "25px",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  }),
};
